/**
 * Minimal RFC 3492 Punycode plus label-wise ToASCII / ToUnicode helpers.
 *
 * This is for DISPLAY and SEARCH NORMALISATION only (showing the U-label next
 * to the A-label, routing `/domains/ví-dụ.vn` and `/domains/xn--...` to the same
 * page). It does not implement full UTS #46 mapping or IDNA2008 validity rules;
 * the backend is authoritative for whether a name is registrable.
 */

const BASE = 36;
const T_MIN = 1;
const T_MAX = 26;
const SKEW = 38;
const DAMP = 700;
const INITIAL_BIAS = 72;
const INITIAL_N = 128;
const DELIMITER = '-';
const MAX_INT = 0x7fffffff;
const ACE_PREFIX = 'xn--';

/** IDNA treats these as label separators in addition to U+002E. */
const DOT_VARIANTS = /[。．｡]/g;

export class PunycodeError extends Error {
  override readonly name = 'PunycodeError';
}

function adapt(delta: number, numPoints: number, firstTime: boolean): number {
  let d = firstTime ? Math.floor(delta / DAMP) : delta >> 1;
  d += Math.floor(d / numPoints);
  let k = 0;
  while (d > ((BASE - T_MIN) * T_MAX) >> 1) {
    d = Math.floor(d / (BASE - T_MIN));
    k += BASE;
  }
  return Math.floor(k + ((BASE - T_MIN + 1) * d) / (d + SKEW));
}

function threshold(k: number, bias: number): number {
  if (k <= bias) return T_MIN;
  if (k >= bias + T_MAX) return T_MAX;
  return k - bias;
}

/** 0..25 -> a..z, 26..35 -> 0..9 */
function digitToBasic(digit: number): string {
  return String.fromCharCode(digit < 26 ? digit + 97 : digit + 22);
}

function basicToDigit(codePoint: number): number {
  if (codePoint >= 0x30 && codePoint <= 0x39) return codePoint - 22; // 0-9 -> 26..35
  if (codePoint >= 0x41 && codePoint <= 0x5a) return codePoint - 0x41; // A-Z
  if (codePoint >= 0x61 && codePoint <= 0x7a) return codePoint - 0x61; // a-z
  return BASE;
}

/** Encode a single Unicode label to its Punycode form (without the `xn--` prefix). */
export function encode(input: string): string {
  const codePoints = Array.from(input, (ch) => ch.codePointAt(0) ?? 0);
  let output = codePoints
    .filter((cp) => cp < 0x80)
    .map((cp) => String.fromCharCode(cp))
    .join('');
  const basicLength = output.length;
  let handled = basicLength;
  if (basicLength > 0) output += DELIMITER;

  let n = INITIAL_N;
  let delta = 0;
  let bias = INITIAL_BIAS;

  while (handled < codePoints.length) {
    let m = MAX_INT;
    for (const cp of codePoints) if (cp >= n && cp < m) m = cp;

    const handledPlusOne = handled + 1;
    if (m - n > Math.floor((MAX_INT - delta) / handledPlusOne)) throw new PunycodeError('overflow');
    delta += (m - n) * handledPlusOne;
    n = m;

    for (const cp of codePoints) {
      if (cp < n && ++delta > MAX_INT) throw new PunycodeError('overflow');
      if (cp === n) {
        let q = delta;
        for (let k = BASE; ; k += BASE) {
          const t = threshold(k, bias);
          if (q < t) break;
          output += digitToBasic(t + ((q - t) % (BASE - t)));
          q = Math.floor((q - t) / (BASE - t));
        }
        output += digitToBasic(q);
        bias = adapt(delta, handledPlusOne, handled === basicLength);
        delta = 0;
        handled++;
      }
    }
    delta++;
    n++;
  }
  return output;
}

/** Decode a single Punycode label (without the `xn--` prefix) to Unicode. */
export function decode(input: string): string {
  const output: number[] = [];
  const basic = Math.max(input.lastIndexOf(DELIMITER), 0);
  for (let j = 0; j < basic; j++) {
    const cp = input.charCodeAt(j);
    if (cp >= 0x80) throw new PunycodeError('not-basic');
    output.push(cp);
  }

  let n = INITIAL_N;
  let bias = INITIAL_BIAS;
  let i = 0;

  for (let index = basic > 0 ? basic + 1 : 0; index < input.length; ) {
    const oldI = i;
    for (let w = 1, k = BASE; ; k += BASE) {
      if (index >= input.length) throw new PunycodeError('invalid-input');
      const digit = basicToDigit(input.charCodeAt(index++));
      if (digit >= BASE) throw new PunycodeError('invalid-input');
      if (digit > Math.floor((MAX_INT - i) / w)) throw new PunycodeError('overflow');
      i += digit * w;
      const t = threshold(k, bias);
      if (digit < t) break;
      if (w > Math.floor(MAX_INT / (BASE - t))) throw new PunycodeError('overflow');
      w *= BASE - t;
    }
    const length = output.length + 1;
    bias = adapt(i - oldI, length, oldI === 0);
    if (Math.floor(i / length) > MAX_INT - n) throw new PunycodeError('overflow');
    n += Math.floor(i / length);
    i %= length;
    output.splice(i++, 0, n);
  }
  return String.fromCodePoint(...output);
}

function splitLabels(domain: string): string[] {
  return domain.trim().replace(DOT_VARIANTS, '.').replace(/\.$/, '').split('.');
}

/** `ví-dụ.vn` -> `xn--v-d-rma6749a.vn`. ASCII labels are lower-cased and passed through. */
export function toASCII(domain: string): string {
  return splitLabels(domain.normalize('NFC').toLowerCase())
    .map((label) => (/[\u0080-\u{10FFFF}]/u.test(label) ? ACE_PREFIX + encode(label) : label))
    .join('.');
}

/** `xn--v-d-rma6749a.vn` -> `ví-dụ.vn`. Labels that fail to decode are returned unchanged. */
export function toUnicode(domain: string): string {
  return splitLabels(domain)
    .map((label) => {
      if (!label.toLowerCase().startsWith(ACE_PREFIX)) return label;
      try {
        return decode(label.slice(ACE_PREFIX.length).toLowerCase());
      } catch {
        return label;
      }
    })
    .join('.');
}

export function isIdn(domain: string): boolean {
  return /[\u0080-\u{10FFFF}]/u.test(domain) || splitLabels(domain).some((l) => l.toLowerCase().startsWith(ACE_PREFIX));
}
